'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from '@/i18n/context';
import { LanguageSwitcher } from '@/components/language-switcher';

export function SiteNav() {
  const t = useTranslations('nav');
  const locale = useLocale();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const NAV = [
    { label: t('story'), href: '#story' },
    { label: t('collections'), href: '#collections' },
    { label: t('reasons'), href: '#reasons' },
    { label: t('scenarios'), href: '#scenarios' },
    { label: t('faq'), href: '#faq' },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-md bg-[color:var(--mp-cream)]/85 border-b hairline'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 md:px-12 lg:px-20">
        <a
          href="#top"
          className="flex items-center gap-2 transition-opacity duration-500 hover:opacity-80"
        >
          <img
            src="/logo.png"
            alt="Moepie"
            className={`h-16 w-auto object-contain transition-all duration-500 ${
              scrolled ? 'opacity-100' : 'brightness-0 invert-[0.95]'
            }`}
          />
          <span
            className={`font-serif-cn text-lg tracking-brand transition-colors duration-500 ${
              scrolled
                ? 'text-[color:var(--mp-forest)]'
                : 'text-[color:var(--mp-cream)]'
            }`}
          >
            {locale === 'zh' && '萌派'}
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={`mp-underline text-[13px] tracking-[0.18em] uppercase transition-colors duration-500 ${
                scrolled
                  ? 'text-[color:var(--mp-forest)]/80 hover:text-[color:var(--mp-forest)]'
                  : 'text-[color:var(--mp-cream)]/80 hover:text-[color:var(--mp-cream)]'
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden md:block">
            <LanguageSwitcher variant={scrolled ? 'light' : 'dark'} />
          </div>
          <a
            href="https://moepie.tmall.com/"
            target="_blank"
            rel="noreferrer noopener"
            className={`hidden md:inline-flex items-center border px-5 py-2 text-[12px] uppercase tracking-[0.22em] transition-colors duration-500 ${
              scrolled
                ? 'hairline text-[color:var(--mp-forest)] hover:bg-[color:var(--mp-forest)] hover:text-[color:var(--mp-cream)]'
                : 'border-[color:var(--mp-cream)]/40 text-[color:var(--mp-cream)] hover:bg-[color:var(--mp-cream)] hover:text-[color:var(--mp-forest)]'
            }`}
          >
            {t('shopNow')}
          </a>

          <button
            type="button"
            aria-label="menu"
            onClick={() => setOpen((v) => !v)}
            className="flex flex-col gap-1.5 md:hidden"
          >
            <span
              className={`h-px w-6 transition-colors duration-500 ${
                scrolled ? 'bg-[color:var(--mp-forest)]' : 'bg-[color:var(--mp-cream)]'
              } ${open ? 'translate-y-[7px] rotate-45' : ''}`}
            />
            <span
              className={`h-px w-6 transition-colors duration-500 ${
                scrolled ? 'bg-[color:var(--mp-forest)]' : 'bg-[color:var(--mp-cream)]'
              } ${open ? 'opacity-0' : ''}`}
            />
            <span
              className={`h-px w-6 transition-colors duration-500 ${
                scrolled ? 'bg-[color:var(--mp-forest)]' : 'bg-[color:var(--mp-cream)]'
              } ${open ? '-translate-y-[6px] -rotate-45' : ''}`}
            />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t hairline bg-[color:var(--mp-cream)] md:hidden">
          <nav className="mx-auto flex max-w-[1440px] flex-col gap-1 px-6 py-6">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="py-3 text-sm tracking-[0.16em] uppercase text-[color:var(--mp-forest)]/80"
              >
                {item.label}
              </a>
            ))}
            <div className="mt-2 flex items-center gap-4 border-t hairline pt-4">
              <LanguageSwitcher variant="light" />
              <a
                href="https://moepie.tmall.com/"
                target="_blank"
                rel="noreferrer noopener"
                className="text-sm uppercase tracking-[0.16em] text-[color:var(--mp-forest)]"
              >
                {t('shopNow')}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
